/**
 * App.tsx — routing, the session bootstrap, and the scope the whole window is on.
 *
 * **Routing is a hash and forty lines**, not a library. The server answers every unknown non-API GET with
 * `index.html`, so path routing would work too — and would buy a dependency, a build-time route manifest
 * and a class of "works in dev, 404s behind the proxy" bugs, in exchange for prettier URLs on an internal
 * tool. The siblings' entire web dependency list is react and react-dom; this one matches.
 *
 * **The bootstrap is `/api/me`, and everything hangs off it.** It answers who the subject is, which scopes
 * they can reach, which administrative sections they may see, and it mints the CSRF token — so a page that
 * has rendered has, by construction, what it needs to act. Nothing it returns is a grant: the sections
 * decide which navigation items are drawn, and the server re-resolves and re-checks every one of them when
 * the button is pressed. **A hidden button is UX; it stops nobody with a terminal.**
 *
 * **The active business unit is window state, held here and pushed into `lib/api.ts` once.** Two rules
 * follow, and both exist because of a confirmed cross-tenant leak in a sibling application:
 *
 *   1. When the subject can reach more than one scope and has not chosen, the answer is **null and a
 *      prompt** — never the first one alphabetically. The server refuses to guess for exactly this reason
 *      and so does the client.
 *   2. Changing the scope **discards every view's data** rather than re-labelling it. A list fetched for
 *      one tenant, still on screen under another tenant's name, is the precise shape of the incident: the
 *      user acts on what they were shown. The remount via `key` is what makes that impossible.
 *
 * The remembered choice lives in `localStorage` and is validated against `accessibleScopes` on every load.
 * It is a convenience, not an authority — a scope the subject can no longer reach is dropped silently,
 * because the alternative is a refusal screen for a choice they made six months ago.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ApiError,
  fetchMe,
  setActiveBu,
  setUnauthenticatedHandler,
  signOut,
  type AppModule,
  type Me,
} from "./lib/api";
import {
  ADMIN_CONSOLE_SECTIONS,
  canReachAdmin,
  visibleAdminSections,
  type AdminConsoleSection,
} from "./lib/admin";
import { applyThemeChoice, readThemeChoice, type ThemeChoice } from "./lib/theme";
import { Shell, primaryNav, type NavItem } from "./components/Shell";
import { AdminConsole, ScopeNeeded } from "./components/AdminConsole";
import { ErrorBoundary } from "./components/ErrorBoundary";
import {
  Button,
  ButtonLink,
  DeniedState,
  EmptyState,
  ErrorState,
  LoadingState,
  Page,
  ToastProvider,
} from "./components/ui";
import { Login } from "./pages/Login";
import { AdminHome } from "./pages/AdminHome";
import { AdminUsers } from "./pages/AdminUsers";
import { AdminAccess } from "./pages/AdminAccess";
import { AdminBusinessUnits } from "./pages/AdminBusinessUnits";
import { AdminAudit } from "./pages/AdminAudit";
import { Dashboard } from "./pages/Dashboard";
import { JobEditor } from "./pages/JobEditor";
import { RunDetailPage, RunHistory } from "./pages/RunHistory";
import { Settings } from "./pages/Settings";
import { Goals } from "./pages/Goals";
import { CloseQuarter } from "./pages/CloseQuarter";
import { GoalDetail } from "./pages/GoalDetail";
import { GoalCheckin } from "./pages/GoalCheckin";

/* ---------------------------------------------------------------------------------------------------
 * Routing
 * ------------------------------------------------------------------------------------------------- */

type Route =
  | { readonly name: "login" }
  /**
   * The application's own root — `#`, `#/`, or no hash at all.
   *
   * A route of its own since 7 August 2026, where it used to be an alias for the job list. It has to be,
   * because the job list is now a MODULE and not every subject holds it: a `goal_author` opening the
   * application at its root would otherwise be refused on arrival, every time, with Goals sitting one
   * click away in their own navigation. `landingRouteFor` resolves it against what the subject actually
   * reaches. An UNKNOWN hash is deliberately NOT this route — it still falls through to `jobs` and is
   * refused there, because a stale link from an email named a place, and answering a named place with a
   * silent redirect somewhere else is a worse answer than saying no.
   */
  | { readonly name: "home" }
  | { readonly name: "jobs" }
  | { readonly name: "job-new" }
  | { readonly name: "job-edit"; readonly jobId: string }
  | { readonly name: "runs"; readonly jobId: string; readonly runId: string | null }
  | { readonly name: "goals" }
  | { readonly name: "goal-detail"; readonly goalId: string }
  | { readonly name: "goal-checkin"; readonly goalId: string }
  | { readonly name: "goal-close-quarter"; readonly goalId: string }
  | { readonly name: "settings" }
  | { readonly name: "admin-home" }
  | { readonly name: "admin-people" }
  | { readonly name: "admin-access" }
  | { readonly name: "admin-business-units" }
  | { readonly name: "admin-audit" }
  /**
   * An address that resolved to nothing — `05-client.md` §5.3, and the arm this union spent its whole
   * life without.
   *
   * It carries two things. **The address**, because a not-found that does not say what was not found is
   * only a redirect with a nicer picture. And **whether it was inside the administration console**,
   * because `02-patterns.md` §2.4 requires the section strip on every route of the module, "its
   * permission-denied and not-found dead ends included" — and the route NAME cannot carry that fact the
   * way every other console route does, since `isAdminRoute` reads the name and a not-found is not an
   * `admin-` route by its name.
   */
  | { readonly name: "not-found"; readonly hash: string; readonly inConsole: boolean };

/** Every job route lights the same navigation item; the editor and the history are not places of their own. */
const JOB_ROUTES: readonly Route["name"][] = ["jobs", "job-new", "job-edit", "runs"];

/** Likewise for goals: the record page and the check-in flow are inside Goals, not beside it. */
const GOAL_ROUTES: readonly Route["name"][] = ["goals", "goal-detail", "goal-checkin", "goal-close-quarter"];

/**
 * The module a route belongs to, or null where it belongs to none.
 *
 * ⚠️ **Derived from the two lists above rather than written out a third time.** They already answer "which
 * navigation item does this route light", and "which module does this route live in" is the same question:
 * a sub-route that lights Automations and is gated as something else is precisely the hole this function
 * exists to close. `#/jobs/:id/runs` is the one that made the point on the server — run history stayed
 * readable to somebody the module itself had been hidden from.
 */
export function moduleForRoute(route: Route): AppModule | null {
  if (JOB_ROUTES.includes(route.name)) return "automations";
  if (GOAL_ROUTES.includes(route.name)) return "goals";
  return null;
}

/** What a module is called on screen. One name for one thing — the heading and the refusal share it. */
const MODULE_TITLE: Readonly<Record<AppModule, string>> = {
  automations: "Automations",
  goals: "Goals",
};

/**
 * Where `home` goes, for this subject.
 *
 * Reads `me.modules` and nothing else, so it cannot disagree with the navigation or with the gate below.
 * The fallback for somebody who holds NEITHER module is `jobs` — not because the job list is a sensible
 * answer for them, but because the module gate turns that route into the no-access landing page, and one
 * refusal drawn in one place is worth more than a second route that draws its own. Administration is
 * deliberately not the fallback either: dropping a person straight into the console would answer "where
 * did my work screens go?" by silently not asking it. The landing page says it, and carries the button.
 */
export function landingRouteFor(me: Me): Route {
  if (me.modules.automations) return { name: "jobs" };
  if (me.modules.goals) return { name: "goals" };
  return { name: "jobs" };
}

/** One route per console section, so the section table and the route union cannot drift apart. */
const ADMIN_ROUTE: Readonly<Record<AdminConsoleSection, Route>> = {
  people: { name: "admin-people" },
  access: { name: "admin-access" },
  "business-units": { name: "admin-business-units" },
  audit: { name: "admin-audit" },
};

/** The console section a route is on, or null for its landing page. Undefined ⇒ not a console route. */
function adminSectionOf(route: Route): AdminConsoleSection | null | undefined {
  if (route.name === "admin-home") return null;
  const spec = ADMIN_CONSOLE_SECTIONS.find((candidate) => ADMIN_ROUTE[candidate.key].name === route.name);
  return spec?.key;
}

function isAdminRoute(route: Route): boolean {
  // A not-found inside the console is a route OF the console — §2.4 counts its dead ends as routes of the
  // module — so it lights the same navigation item and is drawn inside the same frame. It is the one
  // route whose membership the name cannot answer.
  if (route.name === "not-found") return route.inConsole;
  return route.name.startsWith("admin");
}

/**
 * The routes that do NOT belong to a business unit, and so must render before one is chosen.
 *
 * This is not a convenience. A fresh deployment has no units at all, so `activeBu` is null and the
 * "choose a business unit" screen would otherwise be the only thing anybody could ever reach — including
 * the superuser whose job is to create the first one. Administration of people, groups and scopes is
 * genuinely scopeless on the server too (those routes are `SCOPELESS` or read-only), so this list agrees
 * with the API rather than working around it.
 *
 * ⚠️ **`admin-audit` is deliberately absent, and folding the four screens into one console did not
 * change that.** The audit route resolves scope access on the server, so listing it here to make it
 * render early would be exactly the thing `CLAUDE.md` §1c forbids. What the console changed is where the
 * refusal is DRAWN — inside the frame, beside the sections the administrator does hold — not when the
 * screen is mounted. See the console branch below.
 *
 * ⚠️ **The list has TWO consumers now, and until Settings arrived it had one.** `isScopeless` was read
 * only inside the console branch, which is entered only for an `admin-*` route — so a non-console route
 * added to this list would have changed nothing whatever: every other route falls past the console and
 * meets the "choose a business unit" prompt below before any dispatch is reached. Adding `settings` here
 * and stopping would therefore have produced a page a unitless user could never open, with a constant
 * that looked as though it said otherwise. The body branch consults this same predicate, which is what
 * makes the list the single answer to "may this render before a unit is chosen?" rather than a claim.
 */
const SCOPELESS_ROUTES: readonly Route["name"][] = [
  "admin-home",
  "admin-people",
  "admin-access",
  "admin-business-units",
  // Settings is the first entry that is not a console section, and it earns its place on the list's own
  // terms: it reads no business unit's data, issues no request that could name one, and both preferences
  // it offers — the theme, and whether the app is installed — belong to a person and a device. A user who
  // has not been added to a unit yet must still reach it, which is the exact strand this list exists to
  // prevent; and unlike `admin-audit` there is no server-side scope check being worked around here,
  // because there is no request at all.
  "settings",
  // A not-found is on the list for the same reason and a stronger one. It reads no unit's data and
  // issues no request; and the screen exists precisely to say that an address resolves to nothing, so
  // holding it behind "choose a business unit" would answer a mistyped URL with a prompt — the silent
  // redirect §5.3 forbids, wearing a different hat.
  //
  // ⚠️ It has a SECOND consumer, in the console, and the membership is what decides how that fails if
  // the explicit not-found branch there is ever deleted: listed, the route falls to the console's own
  // overview — wrong, and visible to whoever deleted it. Unlisted, it would fall into the scoped branch
  // and mount the AUDIT screen under an address that is not the audit screen's, issuing a scoped read
  // from a route that never resolved. Fail-safe rather than fail-open, exactly as with `admin-audit`.
  "not-found",
];

export function isScopeless(route: Route): boolean {
  return SCOPELESS_ROUTES.includes(route.name);
}

/**
 * `#/jobs/abc/runs` → `{ name: "runs", jobId: "abc" }`. Anything unrecognised is a **not-found**, named
 * as such and carrying the address that produced it.
 *
 * Until 7 August 2026 this function ended `return { name: "jobs" }`, and this docblock's first line said
 * so: "Anything unrecognised lands on the job list." `05-client.md` §5.3 is directly about that line —
 * an interactive app MUST have a not-found state for any route that does not resolve, and it "MUST NOT
 * silently redirect to the home page. A silent redirect and a working link are indistinguishable to the
 * person who followed one, so a mistyped or stale URL becomes 'the app logged me out' or 'the link is
 * broken' — and a bookmark that quietly stopped resolving is never reported, because nothing looked
 * wrong."
 *
 * Three shapes are deliberately NOT unrecognised, and each is a decision rather than an oversight:
 *
 *  1. **The application root** — `""`, `#`, `#/`, `#//` — is the job list, as it always was. It is what
 *     the brand in the top bar links to (`Shell.tsx`: `href="#/"`, and its comment says `parseRoute("")`
 *     is the job list), what a bookmark of the site itself carries, and what the crash screen offers as
 *     the way back. Answering it with "that address matches nothing" would make the application's own
 *     logo a broken link, which is a worse defect than the one being fixed.
 *  2. **A trailing segment on a route that does not read one** — `#/business-units/extra` — still
 *     resolves to the screen its first segment names. Those branches have always matched on `first`
 *     alone; tightening them is a separate decision from this one, and making it here would break
 *     bookmarks quietly on the very change that exists to stop bookmarks breaking quietly.
 *  3. **`#/jobs`** is the list, but `#/jobs/<id>` on its own is NOT. There is no screen at it — the
 *     editor is `…/edit` and the history is `…/runs` — and nothing in the application ever produces it,
 *     so the only way to arrive is by truncating a real address by hand, which is precisely the reader
 *     §5.3 is about.
 *
 * `#/jobs/new` is matched before the `:id` forms and not after: `new` would otherwise be read as a job id
 * and the editor would open in edit mode against a job that does not exist.
 */
export function parseRoute(hash: string): Route {
  const path = hash.replace(/^#/, "").replace(/^\/+/, "");
  const segments = path.split("/").filter((segment) => segment !== "");
  const [first, second, third] = segments;

  // The root, and ONLY the root — `#`, `#/`, `#//`, or no hash at all. Answered first and explicitly,
  // because everything below it falls through to a not-found rather than to the job list; and it is
  // `home` rather than `jobs` because the job list is now a module not every subject holds, so the
  // address a lost user reaches for must not itself be a refusal. See the `home` member on the union.
  if (segments.length === 0) return { name: "home" };

  if (first === "login") return { name: "login" };
  if (first === "admin") {
    if (second === undefined) return { name: "admin-home" };
    const spec = ADMIN_CONSOLE_SECTIONS.find((candidate) => candidate.key === second);
    // An unknown section is a not-found DRAWN INSIDE THE CONSOLE — not a bounce to its landing page. The
    // comment that stood here defended the bounce ("the reader was plainly heading for administration,
    // and the four cards are the answer to a mistyped section"), and that reasoning is the drift itself:
    // it hands the reader a page that looks like it worked, at an address that resolved to nothing, so
    // the stale link in somebody's runbook is never reported. They are still plainly heading for
    // administration, which is why the cards stay one glance away instead of being the answer —
    // `inConsole` keeps the frame, §2.4 keeps the strip, and the page says which address failed.
    return spec === undefined ? { name: "not-found", hash, inConsole: true } : ADMIN_ROUTE[spec.key];
  }
  // The addresses these four screens had before the console existed. Kept working because they are in
  // people's bookmarks and in this repository's own documentation; the address bar is canonicalised to
  // the `#/admin/…` form by the effect in `App`, so nobody keeps a stale link by using one.
  if (first === "people") return ADMIN_ROUTE.people;
  if (first === "access") return ADMIN_ROUTE.access;
  if (first === "business-units") return ADMIN_ROUTE["business-units"];
  if (first === "audit") return ADMIN_ROUTE.audit;
  if (first === "settings") return { name: "settings" };
  /*
   * Goals, and ABOVE the job-list fallback at the foot of this function — which is the whole of what
   * makes these three addresses reachable. `CLAUDE.md` records ~2,000 lines of finished pages shipping
   * unreachable twice over, both times because a page module existed and nothing here routed to it.
   *
   * `#/goals/<id>/checkin` is matched before the bare `:id` form for the same reason `#/jobs/new` is
   * matched before the job editor's: without the ordering, `checkin` is just a third segment nobody
   * reads and the flow's address opens the record page instead.
   */
  if (first === "goals") {
    if (second === undefined) return { name: "goals" };
    if (third === "checkin") return { name: "goal-checkin", goalId: decodeURIComponent(second) };
    // Matched before the bare `:id` form for the same reason `checkin` is: without it, `close-quarter`
    // is a third segment nobody reads and the flow's address opens the record page instead.
    if (third === "close-quarter") return { name: "goal-close-quarter", goalId: decodeURIComponent(second) };
    return { name: "goal-detail", goalId: decodeURIComponent(second) };
  }
  /*
   * The bare job list, named rather than fallen into. Until 2026-08-07 the foot of this function
   * returned it for anything unrecognised, so `#/jobs` never needed an arm of its own; §5.3 replaced
   * that fallback with a not-found, and without this line the application’s main screen would be the
   * first address to stop resolving.
   */
  if (first === "jobs" && second === undefined) return { name: "jobs" };
  if (first === "jobs" && second === "new") return { name: "job-new" };
  if (first === "jobs" && second !== undefined && third === "edit") {
    return { name: "job-edit", jobId: decodeURIComponent(second) };
  }
  if (first === "jobs" && second !== undefined && third === "runs") {
    // A fourth segment addresses one run: a failed run is exactly the thing somebody sends to a colleague,
    // and 04-data.md §1.12 says a detail like that needs an address rather than only an expansion.
    const [, , , fourth] = segments;
    return {
      name: "runs",
      jobId: decodeURIComponent(second),
      runId: fourth === undefined ? null : decodeURIComponent(fourth),
    };
  }
  return { name: "not-found", hash, inConsole: false };
}

/** How much of an unresolved address is echoed back. Long enough to recognise a real one at a glance. */
const MAX_ADDRESS_SHOWN = 96;

/**
 * The unresolved address, shortened for display.
 *
 * ⚠️ **This string is attacker-controlled input arriving from the address bar**, and the not-found screen
 * is the one place in the application that shows a stranger their own text back. Three rules follow, and
 * two of them are enforced by the caller rather than here: it is rendered as a TEXT CHILD and never as
 * HTML, and it never reaches an `href` — a not-found page that links to what was not found is an open
 * redirect and a `javascript:` sink dressed as a courtesy.
 *
 * What this function is for is the third rule: length and control characters. A hash can be a kilobyte
 * long and can carry newlines and C0 bytes, and a sentence with either in it is no longer a sentence.
 */
export function shortAddress(hash: string): string {
  const flattened = hash
    // C0 and DEL first, then every run of whitespace: a newline is both, and collapsing in the
    // other order would leave a tab a browser will happily carry in a fragment sitting in the
    // sentence.
    .replace(/[\u0000-\u001f\u007f]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  return flattened.length <= MAX_ADDRESS_SHOWN ? flattened : `${flattened.slice(0, MAX_ADDRESS_SHOWN)}…`;
}

function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() => parseRoute(window.location.hash));
  useEffect(() => {
    const onHashChange = (): void => setRoute(parseRoute(window.location.hash));
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);
  return route;
}

/* ---------------------------------------------------------------------------------------------------
 * Theme
 * ------------------------------------------------------------------------------------------------- */

const BU_KEY = "ticketless.bu";

/**
 * The viewer's theme CHOICE, which is three-valued.
 *
 * Seeded from what was stored rather than from what the document is currently showing: the attribute
 * cannot distinguish "the viewer asked for light" from "the viewer asked for nothing and the operating
 * system is light", and the old two-valued hook read the attribute and so could never offer the third
 * state back. `lib/theme.ts` owns the key, the attribute and the storage; this holds the React state.
 */
function useThemeChoice(): readonly [ThemeChoice, (choice: ThemeChoice) => void] {
  const [choice, setChoice] = useState<ThemeChoice>(() => readThemeChoice());

  const choose = useCallback((next: ThemeChoice): void => {
    applyThemeChoice(next);
    setChoice(next);
  }, []);

  return [choice, choose] as const;
}

/* ---------------------------------------------------------------------------------------------------
 * A fetched resource, with the four states it is required to have
 * ------------------------------------------------------------------------------------------------- */

interface Resource<T> {
  readonly data: T | null;
  readonly error: ApiError | null;
  readonly loading: boolean;
}

/* ---------------------------------------------------------------------------------------------------
 * Views
 *
 * There is ONE here, and every other screen is a module under `pages/`, taking its data through a prop
 * or a ports object so it can be tested without a network. Three read-only stand-ins once lived here — a
 * job list, a run list and an audit table — written while the real modules sat unimported; they are
 * gone, and the modules they stood in for are below.
 *
 * The exception is `NotFound`, and it is deliberate rather than a first crack in the rule. It is not a
 * screen the router *dispatches to*, it is the router's own answer when there is nothing to dispatch to:
 * it takes no data, issues no request, and its only input is the address that failed — which exists
 * nowhere but here. A module under `pages/` would be a file whose entire content is an argument this
 * function has already made, imported by one caller that is three lines away.
 * ------------------------------------------------------------------------------------------------- */

/**
 * The address that resolved to nothing — `05-client.md` §5.3.
 *
 * `EmptyState` and not `ErrorState`, on purpose. The error state's title is fixed ("That did not work"),
 * it carries `role="alert"`, and it offers a retry — all three are right for something that has just
 * failed and wrong for an address that was never going to resolve. Retrying a typo is not a recovery.
 * The empty state's three parts are exactly `05-content.md` §5's: what belongs here, why it is not here,
 * and the action that fills it.
 *
 * The address is set as a text child of a `<span>` and nowhere else — never into the `href` beside it.
 * See `shortAddress`, which is where that reasoning lives.
 */
function NotFound({ hash }: { readonly hash: string }): JSX.Element {
  return (
    <Page title="Page not found" measure="reading">
      <EmptyState
        title="That address does not match anything here"
        body={
          <>
            Nothing in ticketless is at <span className="mono">{shortAddress(hash)}</span>. The address may
            have been mistyped, or it may be a link from a version of this application that no longer has
            that page. You have not been signed out, and nothing has moved.
          </>
        }
        action={
          <ButtonLink href="#/" variant="primary">
            Go to updates
          </ButtonLink>
        }
      />
    </Page>
  );
}


/* ---------------------------------------------------------------------------------------------------
 * The application
 * ------------------------------------------------------------------------------------------------- */

function rememberedBu(): string | null {
  try {
    return window.localStorage.getItem(BU_KEY);
  } catch {
    return null;
  }
}

function rememberBu(buId: string): void {
  try {
    window.localStorage.setItem(BU_KEY, buId);
  } catch {
    // Storage is a convenience here; without it the choice simply lasts one session.
  }
}

/**
 * What the real query string carries for the sign-in page.
 *
 * `error` is the code the OIDC callback redirects home with. `dev` is the developer's marker: `/api/me`
 * answers 401 to a fresh browser, so `auth.mode` is unknowable at the one moment the development sign-in
 * is wanted, and without a marker it can never be drawn. It publishes nothing — `POST /auth/dev-login`
 * exists only where the development authenticator was built.
 */
export function signInQuery(search: string): { readonly refusal: string | null; readonly dev: boolean } {
  const params = new URLSearchParams(search);
  return { refusal: params.get("error"), dev: params.get("dev") !== null };
}

/**
 * The address a route SHOULD be at, or null where it already is.
 *
 * Only the four legacy administrative hashes need one. Every other route is either already canonical or
 * carries an id this function has no business rebuilding.
 */
export function canonicalHashFor(route: Route): string | null {
  const section = adminSectionOf(route);
  if (section === undefined || section === null) return null;
  return ADMIN_CONSOLE_SECTIONS.find((spec) => spec.key === section)?.href ?? null;
}

export function App(): JSX.Element {
  const route = useRoute();
  const [themeChoice, chooseTheme] = useThemeChoice();
  const [session, setSession] = useState<Resource<Me>>({ data: null, error: null, loading: true });
  const [chosenBu, setChosenBu] = useState<string | null>(null);
  /**
   * The scope whose per-scope re-resolve was REFUSED, if any — see `scopeAnswerPending` below.
   *
   * One piece of state for one distinction the client cannot otherwise make: "the answer for this scope has
   * not arrived yet" and "it arrived as a refusal" both look like `me.activeBu !== activeBu`, and since
   * 7 August 2026 they must be told apart, because the first has to draw a spinner and the second has to
   * draw a refusal. Guessing either way is a real defect: a spinner that never resolves, or — the one that
   * matters — "you have no access in this business unit" flashed at every multi-unit author on every load,
   * for the length of one fetch.
   */
  const [scopeRefused, setScopeRefused] = useState<string | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  const [signIn, setSignIn] = useState(() => signInQuery(window.location.search));

  useEffect(() => {
    if (signIn.refusal === null) return;
    // The refusal must not outlive the sign-in it describes. Nothing else removes it, so a user who was
    // refused, signed in another way, then signed out would meet their old refusal a second time — and it
    // would survive every hash navigation in between. Only `error` is deleted; the hash is preserved, and
    // `replaceState` fires no `hashchange`, so routing is undisturbed.
    const url = new URL(window.location.href);
    url.searchParams.delete("error");
    window.history.replaceState(null, "", url.toString());
  }, [signIn.refusal]);

  useEffect(() => {
    // A bookmark on `#/people` still opens the People roster; the address bar is then rewritten to
    // `#/admin/people` so the next copy-paste carries the current address. Modelled exactly on the
    // `?error=` cleanup above: `replaceState` fires no `hashchange`, so routing is undisturbed.
    const canonical = canonicalHashFor(route);
    if (canonical === null || window.location.hash === canonical) return;
    const url = new URL(window.location.href);
    url.hash = canonical;
    window.history.replaceState(null, "", url.toString());
  }, [route]);

  const resolvedFor = useRef<string | null>(null);

  const loadSession = useCallback((): void => {
    // A full reload starts from the scopeless answer again, so the per-scope re-resolve below must be
    // allowed to run a second time. Without this the window would keep the reloaded session's empty roles.
    resolvedFor.current = null;
    // Same reasoning as `resolvedFor`: a refusal recorded against a scope describes the session that was
    // refused, and this is a new one.
    setScopeRefused(null);
    setSession({ data: null, error: null, loading: true });
    fetchMe()
      .then((me) => {
        setSignedOut(false);
        setSession({ data: me, error: null, loading: false });
      })
      .catch((cause: unknown) => {
        const error =
          cause instanceof ApiError ? cause : new ApiError(0, "internal_error", String(cause), null);
        setSession({ data: null, error, loading: false });
      });
  }, []);

  useEffect(() => {
    // A 401 anywhere means the session is gone and every screen currently drawn is wrong. Handled once,
    // here, rather than by each caller deciding what a lost session looks like.
    setUnauthenticatedHandler(() => {
      setSignedOut(true);
      setSession({ data: null, error: null, loading: false });
      setActiveBu(null);
    });
    loadSession();
  }, [loadSession]);

  const me = session.data;

  /**
   * The scope this window is on.
   *
   * Precedence: an explicit choice, then the server's own selection (which it makes only when there is
   * exactly one reachable scope), then a remembered one that is still reachable. With several reachable
   * scopes and none chosen the answer is null, and the user is asked — never guessed at.
   */
  const activeBu = useMemo<string | null>(() => {
    if (me === null) return null;
    const reachable = new Set(me.accessibleScopes.map((scope) => scope.id));
    if (chosenBu !== null && reachable.has(chosenBu)) return chosenBu;
    if (me.activeBu !== null && reachable.has(me.activeBu)) return me.activeBu;
    const remembered = rememberedBu();
    if (remembered !== null && reachable.has(remembered)) return remembered;
    return null;
  }, [me, chosenBu]);

  /**
   * Pushed into the api module so every request names it — reads included. A read that omits the scope
   * is answered from whichever one the server picked, and a stale tab then shows one tenant under
   * another's heading.
   *
   * ⚠️ **During render, deliberately, and NOT from an effect.** This was a `useEffect` until 6 August
   * 2026, and the ordering was wrong in a way nothing could see until a test mounted `App`: React runs a
   * child's mount effect BEFORE its parent's, so on the commit that first mounts a scoped screen, that
   * screen's own load effect fired while this module still held `null` — and the very first
   * `GET /api/admin/audit` of a session went out with no `X-Ticketless-BU` header at all. Every
   * subsequent request carried it, which is precisely why it survived: the leak is one request wide and
   * on the first paint. Setting it in render puts the value in place before any child renders, let alone
   * mounts. The assignment is a pure, idempotent write of a value derived from state — repeating it on a
   * re-render costs nothing and means nothing — which is what makes it safe to do here.
   */
  setActiveBu(activeBu);

  /**
   * Re-resolve the session **for the chosen scope**, because `roles` is a per-scope answer.
   *
   * The first `/api/me` is deliberately scopeless — it is what tells us which scopes exist. The server
   * therefore answers it with `roles: []` unless the subject happens to have exactly one reachable unit,
   * which is the case it selects for the caller. A subject with two units would otherwise hold empty roles
   * for ever, and every control gated on `job_author` — *New update*, *Edit*, *Run now* — would be invisible
   * to precisely the people whose job it is to use them.
   *
   * `resolvedFor` is what stops this looping. The guard cannot be `me.activeBu !== activeBu` alone: a scope
   * the server declines to resolve comes back with `activeBu` still null, and the effect would ask again on
   * every render for ever. Recording the scope we asked about — not the one we got — makes one request per
   * scope, whatever the answer.
   */
  useEffect(() => {
    if (me === null || activeBu === null) return;
    if (me.activeBu === activeBu || resolvedFor.current === activeBu) return;
    resolvedFor.current = activeBu;

    let live = true;
    fetchMe(undefined, activeBu)
      .then((scoped) => {
        // No loading state on purpose: the window is already correct, this only sharpens what it may offer.
        if (live) setSession({ data: scoped, error: null, loading: false });
      })
      .catch(() => {
        // A refusal here is not fatal — it means this scope grants nothing, which the scopeless answer
        // already described. Leaving the session as it stands shows fewer controls, never more. What is
        // recorded is that the question has been ASKED AND ANSWERED for this scope, so the module gate
        // stops waiting and says so; without it the reader would hold a spinner for ever.
        if (live) setScopeRefused(activeBu);
      });
    return () => {
      live = false;
    };
  }, [me, activeBu]);

  const selectBu = useCallback(
    (buId: string): void => {
      setChosenBu(buId);
      rememberBu(buId);
      // Back to the list, but ONLY from a job route: a deep link into one tenant's job is meaningless in
      // another's, and answering it with that tenant's 404 is a worse answer than not asking the
      // question. That reasoning is about job records and reaches no further. The console's screens are
      // either scopeless or re-read for the new unit, and throwing an administrator out to Automations for
      // answering a prompt the console itself put to them would be a worse answer still.
      //
      // The ROOT rather than `#/jobs` since 7 August 2026: which modules a subject reaches is a per-scope
      // answer too, so the tenant they have just switched to may not be one where Automations is theirs.
      // `#/` resolves against the new unit's grants once they arrive; naming the module would refuse them
      // for answering a question the switcher asked.
      if (JOB_ROUTES.includes(route.name)) window.location.hash = "#/";
      // The same reasoning, and the same narrowness: a goal id belongs to one business unit's
      // registrations, so a deep link into one tenant's objective is meaningless in another's. The
      // check-in flow is the sharper case — it would otherwise re-issue `POST …/checkins` under the new
      // tenant against an id that tenant has never heard of.
      if (GOAL_ROUTES.includes(route.name)) window.location.hash = "#/";
    },
    [route.name],
  );

  const onSignOut = useCallback((): void => {
    void signOut()
      .catch(() => {
        // The cookie is cleared by the server on any outcome it can reach; a failure here means the
        // request never landed, and re-reading the session is the honest way to find out where we stand.
      })
      .finally(() => {
        setSignedOut(true);
        setSession({ data: null, error: null, loading: false });
        setActiveBu(null);
        // A refusal from a previous sign-in is not a description of this sign-out.
        setSignIn((current) => ({ refusal: null, dev: current.dev }));
        window.location.hash = "#/login";
      });
  }, []);

  if (session.loading) {
    return (
      <div className="login">
        {/* Nothing is being signed in here — the session is being read. */}
        <LoadingState label="Loading" rows={2} />
      </div>
    );
  }

  // Not authenticated, or asked to sign in. `not_admitted` is deliberately NOT treated as a session
  // failure: the account exists and is in order, it simply has not been let in yet.
  const unauthenticated =
    signedOut ||
    me === null ||
    (session.error !== null && session.error.status === 401) ||
    route.name === "login";

  if (unauthenticated) {
    // A live session that is no longer admitted — suspended after signing in — is the same refusal as a
    // callback that redirected with `?error=not_admitted`. One card says it, not two: a second hand-rolled
    // auth-error page is how the two drift apart, and it had already started to.
    const sessionRefusal =
      session.error !== null && session.error.code === "not_admitted" ? "not_admitted" : null;
    return (
      <Login
        mode={me?.auth.mode ?? "oidc"}
        refusal={sessionRefusal ?? signIn.refusal}
        devOffered={signIn.dev}
        {...(sessionRefusal === null ? {} : { onRecheck: loadSession })}
        onSignedIn={() => {
          setSignIn((current) => ({ refusal: null, dev: current.dev }));
          // The ROOT, not the job list. Whoever has just signed in may hold Goals and not Automations —
          // sending them to a named module would refuse them on the first screen of the session.
          window.location.hash = "#/";
          loadSession();
        }}
      />
    );
  }

  if (session.error !== null || me === null) {
    const error = session.error ?? new ApiError(0, "internal_error", "no session", null);
    return (
      <div className="login">
        <ErrorState message={error.userMessage} traceId={error.traceId} onRetry={loadSession} />
      </div>
    );
  }

  /**
   * Three entries at most, where there were five — and the gate lives in `Shell.tsx` beside the item it
   * draws, not here. Restating it in this file is what let a test assert a transcription of it while the
   * real expression went unguarded; see `primaryNav`'s own note.
   */
  const nav: readonly NavItem[] = primaryNav(me);

  /**
   * The route this render is actually SHOWING, which is the parsed one except at the root.
   *
   * `home` is resolved here rather than by rewriting the address, and the difference matters. A redirect
   * would put `#/` into the history behind `#/jobs`, so Back would land on `#/` and be redirected forward
   * again — the reader would be unable to leave. Resolving it in place leaves the address as the reader
   * typed it and costs nothing: `#/` has never named a screen of its own.
   */
  const view: Route = route.name === "home" ? landingRouteFor(me) : route;

  const activeKey = isAdminRoute(view)
    ? "admin"
    : JOB_ROUTES.includes(view.name)
      ? "jobs"
      : GOAL_ROUTES.includes(view.name)
        ? "goals"
        : view.name;

  const activeScopeName =
    me.accessibleScopes.find((scope) => scope.id === activeBu)?.displayName ?? null;

  /**
   * True while the answer in hand was resolved for a DIFFERENT scope than the one this window is on, and
   * the re-resolve for this one has neither landed nor been refused.
   *
   * The same guard `AccountMenu` puts on its role line, for a sharper consequence. `roles` — and now
   * `modules` — are per-scope, and the bootstrap `/api/me` is deliberately scopeless: a subject with two
   * units holds `roles: []` until the second fetch lands. Before 7 August 2026 that briefly hid a button.
   * It now briefly hides every work surface they have, and the module gate below would state that as a
   * fact — "you have no access in this business unit" — at every multi-unit author, on every load. So the
   * gate draws a spinner instead while this is true, and only says no once there is an answer to say it
   * from. `scopeRefused` is what stops the spinner being for ever: a refused re-resolve IS an answer.
   */
  const scopeAnswerPending =
    activeBu !== null && me.activeBu !== activeBu && scopeRefused !== activeBu;

  /**
   * The landing page for a subject who reaches **neither** work module in this business unit.
   *
   * New on 7 August 2026, and it exists because the rule that made modules invisible also made an empty
   * primary navigation possible for the first time. Four things it has to do, and a bare denial does none
   * of them:
   *
   *   1. **Name the business unit.** Roles are per scope, so "you have no access" without a unit named is
   *      not merely vague, it is ambiguous between "here" and "anywhere" — and the remedy differs.
   *   2. **Say where access comes from**: a group, granted by an administrator. That is the sentence that
   *      turns a dead end into a request somebody can actually make, and it is the same sentence the
   *      business-unit prompt and the console's own refusal already use.
   *   3. **Not strand an administrator.** Somebody holding a console section but no scope role — an
   *      auditor, most obviously — has somewhere to be, and the button says so. They are not dropped
   *      there automatically: they would never learn why the work screens went.
   *   4. **Point at the switcher** where there is more than one unit to switch to, since the answer may
   *      simply be that this window is on the wrong one.
   */
  const noModulesLanding = ((): JSX.Element => {
    const unit = activeScopeName ?? "this business unit";
    const alsoAdmin = canReachAdmin(me);
    const sentences = [
      `Automations and Goals are each carried by a group, never by your account itself — an administrator has to add you to a group that grants you a role in ${unit}.`,
      ...(me.accessibleScopes.length > 1
        ? ["If you work in more than one business unit, the switcher in the top bar changes which one this is."]
        : []),
      ...(alsoAdmin ? ["Administration is open to you in the meantime."] : []),
    ];
    return (
      <Page title={`No access in ${unit}`} measure="reading">
        <DeniedState
          title="There is nothing here for you yet"
          body={sentences.join(" ")}
          {...(alsoAdmin
            ? {
                // A link and not a button, like the console's own way out: it is navigation, so it must
                // survive a middle-click and show its destination in the status bar.
                action: (
                  <a className="btn btn--primary" href="#/admin">
                    <span className="btn__label">Go to administration</span>
                  </a>
                ),
              }
            : {})}
        />
      </Page>
    );
  })();

  /**
   * What the dispatch draws in place of a module this subject cannot reach.
   *
   * Three answers, and the order is the whole of the care here. **Pending first**: while the per-scope
   * answer is still in flight there is no refusal to make, and stating one would accuse a genuine author of
   * having no access for the length of a fetch. **Then the landing page**, when they reach neither module —
   * a person with an empty navigation needs somewhere to be, not a second copy of the word "no". **Then the
   * ordinary refusal**, which is the case where they hold the other module: they have a navigation, they
   * are not stranded, and what they need is to be told plainly that this one is not theirs.
   */
  // A `const` arrow rather than a hoisted `function`: a declaration could be called above the `me === null`
  // return, so TypeScript declines to narrow `me` inside one — correctly, and this is the shape that says
  // "only after the session is known".
  const moduleRefusal = (module: AppModule): JSX.Element => {
    const title = MODULE_TITLE[module];
    if (scopeAnswerPending) {
      return (
        <Page title={title}>
          <LoadingState label="Loading" rows={3} />
        </Page>
      );
    }
    if (!me.modules.automations && !me.modules.goals) return noModulesLanding;
    return (
      <Page title={title}>
        <DeniedState
          title={`You do not have access to ${title}`}
          body={`${title} is carried by a group in this business unit, never by your account itself. Ask an administrator to add you to one that grants a role in it.`}
        />
      </Page>
    );
  };

  /**
   * The administration console.
   *
   * Three properties this arrangement preserves, each of which was a live rule before the console
   * existed and none of which the console was allowed to relax:
   *
   *  1. **`admin-audit` is not in `SCOPELESS_ROUTES`.** `isScopeless` is now the single predicate that
   *     decides whether a section may mount before a unit is chosen — the console CONSULTS it rather
   *     than enumerating the scopeless three by hand, which is what the old switch did.
   *  2. **`AdminAudit` is never mounted without a scope**, so it never issues `GET /api/admin/audit`
   *     with no `X-Ticketless-BU` header. That is the leak shape the whole scoping arrangement exists
   *     to prevent.
   *  3. **The administrator is not stranded.** The refusal is drawn INSIDE the frame, so the strip is
   *     still on screen and the other three sections are one click away — where the shell-level "Choose
   *     a business unit" branch would have replaced the entire body and taken them with it.
   *
   * Every screen below is unchanged; only where they are reached from has moved.
   */
  const consoleBody = ((): JSX.Element | null => {
    if (!isAdminRoute(view)) return null;

    const visible = visibleAdminSections(me);
    const section = adminSectionOf(view);

    const inner = ((): JSX.Element => {
      if (visible.length === 0) {
        // A deep link from somebody holding no section at all. Refused here as well as on the server,
        // because hiding and refusing must agree: the entry is not drawn, and the address behind it
        // says the same thing rather than answering with a blank page.
        return (
          <Page title="Administration">
            <DeniedState body="Administration is carried by a group. Ask an administrator to add you to one that grants a section." />
          </Page>
        );
      }

      /**
       * An address inside the console that resolves to nothing — §5.3 — answered HERE, so §2.4's "every
       * route of the module, its permission-denied and not-found dead ends included" holds and the strip
       * stays on screen with the sections this caller does hold.
       *
       * Two orderings in this block are decisions. It comes AFTER the refusal above, because a caller
       * holding no section must meet the same answer at every administrative address: whether
       * `#/admin/people` names a real section is not theirs to learn, which is §5.3's closing paragraph
       * handing that choice to the permission model. And it comes BEFORE the scoped/scopeless fork
       * below, because a not-found is neither — it reads nothing, so asking it to choose a business unit
       * first would be the redirect this whole change removes, one screen further in.
       */
      if (route.name === "not-found") return <NotFound hash={route.hash} />;

      /**
       * ⚠️ **The mount condition for a scoped console section, and `isScopeless` is what decides which
       * sections those are.** This is where that predicate finally earns its place: before the console
       * existed the three scopeless screens were enumerated by hand in a switch, and `isScopeless` was
       * consulted by nothing but its own test — a constant asserted against itself. It is live now.
       *
       * `AdminAudit` is mounted on exactly one path, and that path has already established a unit, so
       * it can never issue `GET /api/admin/audit` without an `X-Ticketless-BU` header. Adding
       * `admin-audit` to `SCOPELESS_ROUTES` — the edit `CLAUDE.md` §1c forbids — does not produce that
       * leak either: the route falls through to the scopeless switch, which has no case for it, and the
       * reader gets the landing page instead of the audit screen. Wrong, caught by
       * `App.mount.test.tsx`, and fail-safe rather than fail-open.
       */
      if (!isScopeless(view)) {
        if (activeBu === null) {
          return (
            <ScopeNeeded
              scopes={me.accessibleScopes}
              onSelectBu={selectBu}
              canCreateBusinessUnit={me.sections.business_units.view}
            />
          );
        }
        // Audit is the console's only scoped section, so `activeBu` is a string from here.
        return <AdminAudit buId={activeBu} canView={me.sections.audit.view} />;
      }

      switch (view.name) {
        case "admin-people":
          return (
            <AdminUsers
              canView={me.sections.users.view}
              // Every mutation on this surface is superuser-only by the delegation map, not by section:
              // a delegate holding `users.act` still cannot admit anybody. Drawing the buttons from the
              // section would offer a control that always 403s, which is a worse lie than a missing one.
              canAct={me.user.isSuperuser}
              canAssign={me.user.isSuperuser}
              requiresBusinessUnit={me.requiresBusinessUnit}
            />
          );
        case "admin-access":
          return (
            <AdminAccess
              canView={me.sections.access.view}
              canAct={me.user.isSuperuser}
              scopes={me.accessibleScopes}
            />
          );
        case "admin-business-units":
          return (
            <AdminBusinessUnits
              canView={me.sections.business_units.view}
              canManage={me.user.isSuperuser}
              // Creating or enabling a unit changes what this very session can reach, so the Decision is
              // re-fetched rather than inferred. The server is re-resolving anyway; this only stops the
              // switcher from lagging a page behind the truth.
              onChanged={loadSession}
            />
          );
        default:
          // Same flag the four screens' own action props are drawn from, and for the same reason: every
          // administrative verb here is superuser-only by omission from `DELEGATE_ROUTES`, so a card
          // that described the verbs to a delegate would be promising what the screen then refuses.
          return (
            <AdminHome
              sections={visible}
              activeScopeName={activeScopeName}
              canChange={me.user.isSuperuser}
            />
          );
      }
    })();

    // `section` and not `section ?? null`: the three states are distinct and the strip renders all three
    // differently. A section key marks that tab; `null` is the landing page and marks Overview;
    // `undefined` is a console address that resolved to nothing, where NO tab is the page the reader is
    // on. Collapsing the third into the second — which is what `?? null` did until 7 August 2026 — put
    // `aria-current="page"` on Overview while the body said the address matched nothing, so the
    // navigation and the page contradicted each other and the navigation was the one that lied.
    return (
      <AdminConsole sections={visible} active={section}>
        {inner}
      </AdminConsole>
    );
  })();

  const body = ((): JSX.Element => {
    if (consoleBody !== null) return consoleBody;

    /**
     * The address that resolved to nothing, answered above the business-unit prompt for the same reason
     * Settings is: it reads no unit's data and issues no request.
     *
     * Answering `#/nonsense` with "choose a business unit" would be a redirect with an extra step in it,
     * and a redirect the reader blames on their session rather than on their address — the §5.3 failure
     * in its most confusing form. A console address returned above, inside the frame.
     */
    if (route.name === "not-found") return <NotFound hash={route.hash} />;

    /**
     * The scopeless screens that are NOT console sections — Settings, and nothing else today.
     *
     * It has to be answered ABOVE the business-unit prompt, and `isScopeless` is what says so: the same
     * predicate the console consults, so the question "may this render before a unit is chosen?" keeps
     * one answer in one place instead of acquiring a private copy here. Getting this wrong is not
     * cosmetic — a person nobody has added to a business unit yet would be held on the prompt, unable to
     * reach the one screen in the application that is genuinely theirs.
     *
     * The check on the route name is not redundant belt and braces. Every console section returned above,
     * so the only scopeless route that can reach this line is one this branch knows about — and a future
     * scopeless screen added to the list but forgotten here falls through to the prompt, which is
     * visibly wrong to whoever added it, rather than silently rendering Settings under its name.
     */
    if (isScopeless(view) && view.name === "settings") {
      // Same state as the account menu's quick toggle, from the same hook: `App` holds the choice,
      // `lib/theme.ts` owns the key and the attribute, and neither control owns anything of its own.
      return <Settings themeChoice={themeChoice} onChooseTheme={chooseTheme} />;
    }

    if (activeBu === null) {
      const noUnitsExist = me.accessibleScopes.length === 0;
      return (
        <Page title="Choose a business unit" measure="reading">
          <EmptyState
            title={noUnitsExist ? "You are not a member of any business unit" : "Choose a business unit to continue"}
            body={
              noUnitsExist
                ? me.sections.business_units.view
                  ? "Membership is carried by a group, never by your account itself. If this deployment is new, no business unit has been created yet — start there."
                  : "Membership is carried by a group, never by your account itself. An administrator has to add you to one."
                : "Every screen here belongs to exactly one business unit, and nothing is shown until you say which. Use the switcher in the top bar."
            }
            {...(noUnitsExist && me.sections.business_units.view
              ? {
                  action: (
                    <Button variant="primary" onClick={() => (window.location.hash = "#/admin/business-units")}>
                      Go to business units
                    </Button>
                  ),
                }
              : {})}
          />
        </Page>
      );
    }

    /**
     * ⚠️ **The module gate — the dispatch REFUSING, where the navigation merely did not offer.**
     *
     * Hiding and refusing must agree, and the address bar is the half that gets forgotten: `parseRoute`
     * falls through to `{ name: "jobs" }` for any unknown hash, so a link in a six-month-old email lands
     * here too. Without this branch such a reader would get the Automations screen, whose own fetch is
     * refused, and the result on screen would be an EMPTY LIST — "your updates are gone" told to somebody
     * whose updates are merely none of their business. `ui.tsx` says it plainly: rendering empty where the
     * real answer is "not allowed" is the single easiest way to hide a permissions bug from the people who
     * could report it.
     *
     * It reads `me.modules` — the server's answer — and re-derives nothing. `moduleForRoute` covers every
     * sub-route of both modules, not just their landing pages, because the sub-routes are where the server
     * had left the hole: run history was readable to somebody the module itself had been hidden from.
     */
    const required = moduleForRoute(view);
    if (required !== null && !me.modules[required]) return moduleRefusal(required);

    /**
     * Authoring is a **per-scope role**, not a section and not the superuser flag.
     *
     * `sections.jobs` exists in the enum and is consulted by no route on the server, so a button drawn from
     * it would be a guess. And `isSuperuser` is not a shortcut either: the resolver hands a superuser every
     * scope role only when the request named a scope they can actually reach, so OR-ing it in would be right
     * by accident and wrong on a disabled unit. The roles for *this* scope are the honest predicate, and the
     * effect above is what makes them arrive.
     *
     * ⚠️ **Two roles now, and they are not interchangeable.** Goals borrowed `job_author` until 7 August
     * 2026; its writes are `goal_author`'s and `requireScopeRole` is literal, so a subject holding one and
     * not the other must be offered exactly the verbs they hold. Note what this is NOT: it is not the module
     * rule re-derived — reaching a module is `me.modules`, decided above and by the server. This is the
     * ordinary per-role gate on the verbs INSIDE a module the subject has already been admitted to, which
     * is what `me.roles` has always been for. A `bu_admin` reaches both modules and authors in neither.
     */
    const canAuthorJobs = me.roles.includes("job_author");
    const canAuthorGoals = me.roles.includes("goal_author");

    switch (view.name) {
      case "job-new":
      case "job-edit":
        if (!canAuthorJobs) {
          return (
            <Page title="Automations">
              <DeniedState body="Creating and editing updates needs the job-author role in this business unit. It is carried by a group, never by the account itself." />
            </Page>
          );
        }
        return (
          <JobEditor
            buId={activeBu}
            jobId={view.name === "job-edit" ? view.jobId : null}
            // Strictly more than authoring: choosing whose authority an update borrows is its own role.
            canReassign={me.roles.includes("bu_admin")}
            onSaved={(jobId) => {
              window.location.hash = `#/jobs/${encodeURIComponent(jobId)}/runs`;
            }}
            onCancel={() => {
              window.location.hash = "#/jobs";
            }}
            onDeleted={() => {
              window.location.hash = "#/jobs";
            }}
          />
        );
      case "goals":
        return <Goals buId={activeBu} canAuthor={canAuthorGoals} />;
      case "goal-detail":
        return <GoalDetail buId={activeBu} goalId={view.goalId} canAuthor={canAuthorGoals} />;
      case "goal-checkin":
        // Every call this screen makes on arrival is a write — `POST …/checkins` opens or resumes one —
        // so unlike the list and the record page there is nothing here for a non-author to read. Refused
        // in the same shape as the editor above, and re-checked by the server on every one of them.
        //
        // `goal_author` since 7 August 2026, and NOT `job_author`: `POST …/checkins` requires it by name
        // and `requireScopeRole` is literal, so a subject reaching Goals as a `bu_admin` is offered the
        // flow the server will refuse unless this names the role the server actually asks for.
        if (!canAuthorGoals) {
          return (
            <Page title="Check-in">
              <DeniedState body="Running a check-in needs the goal-author role in this business unit. It is carried by a group, never by the account itself." />
            </Page>
          );
        }
        return <GoalCheckin buId={activeBu} goalId={view.goalId} />;
      case "goal-close-quarter":
        // Same shape as the check-in above and for the same reason: arriving here POSTs a closure, so
        // there is nothing for a non-author to read. `goal_author`, because that is the role every one
        // of the six closure routes asks for by name.
        if (!canAuthorGoals) {
          return (
            <Page title="Close quarter">
              <DeniedState body="Closing a quarter needs the goal-author role in this business unit. It is carried by a group, never by the account itself." />
            </Page>
          );
        }
        return <CloseQuarter buId={activeBu} goalId={view.goalId} canAuthor={canAuthorGoals} />;
      case "runs":
        // ⚠️ ONE ADDRESS, TWO SCREENS — and the run detail is the second of them rather than a disclosure
        // inside the first. `04-data.md` §1.12's settling test is that a detail somebody would send to a
        // colleague is a page; this one is two tables and a provenance list, and it had an address before
        // it had a page. Naming a run therefore REPLACES the list rather than appending to it or covering
        // it, which is what removed both the scroll-to-find-it and the deviation behind it.
        return view.runId === null ? (
          <RunHistory
            buId={activeBu}
            jobId={view.jobId}
            canRun={canAuthorJobs}
            onBack={() => {
              window.location.hash = "#/jobs";
            }}
          />
        ) : (
          <RunDetailPage
            buId={activeBu}
            jobId={view.jobId}
            runId={view.runId}
            onBack={() => {
              window.location.hash = `#/jobs/${encodeURIComponent(view.jobId)}/runs`;
            }}
          />
        );
      // `login` cannot reach here: the unauthenticated branch above returns before this point, and the
      // compiler knows it — adding a case for it is an error, not belt and braces. Every `admin-*` route
      // returned above, inside the console, and `settings` and `not-found` returned above it. `settings`
      // has no case here on purpose: the only way it reaches this switch is somebody dropping it from
      // `SCOPELESS_ROUTES`, and landing on the job list is a wrong answer somebody notices — where a case
      // here would render Settings anyway and hide the fact that the constant no longer means anything.
      // `not-found` has no case for the same reason, and one more: this default is the very behaviour
      // §5.3 forbids, so it must never become an address's real answer again.
      case "jobs":
      default:
        return (
          <Dashboard
            buId={activeBu}
            canAuthor={canAuthorJobs}
            onCreate={() => {
              window.location.hash = "#/jobs/new";
            }}
            onEdit={(jobId) => {
              window.location.hash = `#/jobs/${encodeURIComponent(jobId)}/edit`;
            }}
          />
        );
    }
  })();

  return (
    <ToastProvider>
      {/* No theme here any more: the choice belongs to Settings, which offers all three states, and the
          account menu links to it rather than carrying a copy of the control. `App` still holds the
          state, because `Settings` is a page and pages here own no state of their own. */}
      <Shell
        me={me}
        nav={nav}
        activeKey={activeKey}
        activeBu={activeBu}
        onSelectBu={selectBu}
        onSignOut={onSignOut}
      >
        {/*
          The INNER of the two boundaries, and its position is the whole of its value — `05-client.md`
          §5.1: the boundary "SHOULD be placed so that a failure in one region does not take the whole
          application with it. An error in a chart should not remove the navigation the user needs to get
          away from it." Inside the shell, so a page that throws leaves the rail, the tab bar, the unit
          switcher and the account menu on screen and one click away from somewhere that works. The outer
          one is in `main.tsx` and catches the shell itself.

          The key moved from the div onto the boundary and still does everything the div's key did:
          remounting on the scope discards every view's data, because a list fetched for one tenant and
          still on screen under another tenant's name is the precise shape of the incident this
          application is built around. It now also clears a crash when the scope changes — which the
          keyed div could not, since the boundary above it kept its error state and the reader stayed on
          the failure screen whichever unit they switched to.
        */}
        <ErrorBoundary region="page" key={activeBu ?? "none"}>
          <div>{body}</div>
        </ErrorBoundary>
      </Shell>
    </ToastProvider>
  );
}
